import { Injectable } from "@angular/core"
import { isEqual } from "lodash-es"
import { ReplaySubject, asyncScheduler, first, forkJoin, map, observeOn, of, shareReplay } from "rxjs"
import { StateStorageOptions, StateStore } from "./state-storage.model"

@Injectable({
  providedIn: 'root'
})
export class StateStorageFactory {
  create<T>(options: StateStorageOptions<T>): StateStore<T> {
    return new DefaultStateStore(options)
  }
}

class DefaultStateStore<T> implements StateStore<T> {
  private stateSubject = new ReplaySubject<T>(1)
  readonly state$ = this.stateSubject.asObservable()

  private currentState: T
  private hydrated = false

  constructor(private options: StateStorageOptions<T>) {
    this.currentState = options.defaultState
    this.loadInitialState()
  }

  getState(): T {
    return this.currentState
  }

  setState(nextState: T) {
    if (isEqual(nextState, this.currentState)) {
      return of(void 0)
    }
    this.currentState = nextState
    this.hydrated = true
    this.stateSubject.next(nextState)
    const write$ = this.writeState(nextState).pipe(
      first(),
      shareReplay({ bufferSize: 1, refCount: false })
    )
    // Делаем запись "горячей": запускаем побочный эффект сразу
    write$.subscribe()
    return write$
  }

  dispose(): void {
    this.stateSubject.complete()
  }

  private readState() {
    if (this.options.adapters.length === 0) {
      return of(this.options.defaultState).pipe(observeOn(asyncScheduler))
    }
    return forkJoin(this.options.adapters.map(adapter => adapter.read())).pipe(
      map(states => states.find(state => state !== undefined && state !== null) ?? this.options.defaultState),
      observeOn(asyncScheduler)
    )
  }

  private writeState(state: T) {
    if (this.options.adapters.length === 0) {
      return of(void 0)
    }
    return forkJoin(this.options.adapters.map(adapter => adapter.write(state))).pipe(
      map(() => void 0)
    )
  }

  private loadInitialState(): void {
    this.readState().pipe(first()).subscribe(storedState => {
      if (this.hydrated) {
        return
      }
      this.currentState = this.normalizeState(storedState)
      this.hydrated = true
      this.stateSubject.next(this.currentState)
    })
  }

  private normalizeState(parsed: unknown): T {
    if (this.options.normalize) {
      return this.options.normalize(parsed, this.options.defaultState)
    }
    return parsed as T
  }
}
